/**
 * MONSTER TOWN — QA SURFACE FOR THE DAY. ROUTINE CREW (src/routine/**).
 *
 * Extends the harness by REGISTERING only (`registerAngle` / `registerDemo`),
 * exactly like `src/npc/demos.ts`; it never changes a name another tool is
 * written against.
 *
 * `window.__mtDay` is this crew's own debug window, in the shape of
 * `window.__tut`: nothing in the shipped game reads it, and it exists so
 * `tools/routine-check.mjs` can assert what the neighbours are ACTUALLY doing
 * rather than assert that a function was called.
 *
 * A DEMO THAT LIES IS WORSE THAN NO DEMO — `routine-night` returns how many
 * neighbours are genuinely off the street (hidden art), not how many were told
 * to go home.
 */

import { clock } from '../core/time'
import { registerAngle, registerDemo, settle } from '../harness'
import { npcs, townCentre, townNode } from '../npc/registry'
import { routineReport, stuckReport, sweepNow, type RoutineReport, type StuckReport } from './director'
import { bubbleState, offerNow } from './invitations'
import { clearanceAt, placeSpot, standable } from './places'
import type { NeedId } from '../core/schedule'

export type BubbleReport = ReturnType<typeof bubbleState>

export interface DayTestApi {
  /** What every neighbour is doing right now, and whether they are indoors. */
  report(): RoutineReport[]
  /** Re-run the whole routine pass now. Returns how many changed activity. */
  sweep(snap?: boolean): number
  /** How many neighbours are out on the street (visible) vs gone in. */
  street(): { outdoors: number; indoors: number; total: number }
  /** Put the clock somewhere and settle the town into that hour in one call. */
  setHour(hour: number): number
  /** Force a gentle invitation, ignoring its cooldown. */
  invite(id: NeedId): BubbleReport
  bubble(): BubbleReport
  /** Resolve a place tag against the town AS THE KID HAS BUILT IT. */
  spot(where: string): { x: number; z: number } | null
  /** Can a monster stand on this world point in the town right now? */
  walkable(x: number, z: number): boolean
  /**
   * How far the nearest thing a monster cannot walk on is, in metres, capped at
   * `max`. 0 means standing in it. A DISTANCE, because the yes/no above is
   * answered off a 1 m mask and rounds in the game's favour — which is how a
   * monster ended up in the fountain with every gate green.
   */
  clearance(x: number, z: number, max?: number): number
  /** The closest two visible neighbours in the town, and how close they are. */
  spacing(): { closest: number; pair: string; outdoors: number }
  /**
   * Register a one-off camera angle on a world point and hand back its name, so
   * a tool can PHOTOGRAPH the exact spot its numbers just complained about
   * rather than photograph the middle of town and hope. QA only.
   *
   * `zoom` is an index into the camera's fixed ladder (0 = closest), never a
   * free zoom — there is no free camera in this game and a QA hook is not
   * allowed to invent one.
   */
  frame(x: number, z: number, zoom?: number): string | null
  /** Every reason the tidy pass has for leaving somebody where they are. */
  stuck(): StuckReport[]
}

declare global {
  interface Window {
    __mtDay?: DayTestApi
  }
}

let registered = false

export function registerRoutineHarness(): void {
  if (!registered) {
    registered = true
    window.__mtDay = {
      report: () => routineReport(),
      sweep: (snap = true) => sweepNow(snap),
      street: () => {
        let indoors = 0
        for (const n of npcs.values()) if (n.indoors) indoors++
        return { outdoors: npcs.size - indoors, indoors, total: npcs.size }
      },
      setHour: (hour) => {
        const h = clock.setHour(hour).hour
        sweepNow(true)
        return h
      },
      invite: (id) => {
        offerNow(id)
        return bubbleState()
      },
      bubble: () => bubbleState(),
      spot: (where) => {
        const n = townNode()
        if (n === null) return null
        const p = placeSpot(n, where)
        return p === null ? null : { x: p.x, z: p.z }
      },
      /*
       * THE GATE'S QUESTION HAS TO BE THE GAME'S QUESTION.
       *
       * This used to ask the placement grid alone — the same half-question
       * `walkableNear` was asking — so `routine-check`'s "no neighbour is ever
       * left standing somewhere unwalkable" passed with a monster in the pond
       * and another in the fountain. A tool that shares a blind spot with the
       * code it is checking cannot see anything. `standable()` is now the ONE
       * definition, used by the director and by the gate.
       *
       * AND IT STILL SHARED ONE, for a whole round: `standable()` answered off a
       * one-metre mask sampled at cell centres, so it said yes with a monster's
       * centre up to half a metre inside the fountain. Both were fixed together
       * — the game's question grew a body (`BODY` in `places.ts`) and the gate
       * grew `clearance()` below, which is a DISTANCE and cannot round a monster
       * out of the water.
       */
      walkable: (x, z) => {
        const n = townNode()
        if (n === null) return false
        return standable(n.grid, x, z)
      },
      clearance: (x, z, max = 2.5) => {
        const n = townNode()
        if (n === null) return 0
        return clearanceAt(n.grid, x, z, max)
      },
      frame: (x, z, zoom = 1) => {
        const n = townNode()
        if (n === null) return null
        const name = 'routine-probe'
        registerAngle({ name, sceneId: n.def.id, target: { x, y: 0, z }, rot: 0, zoom })
        return name
      },
      stuck: () => stuckReport(),
      spacing: () => {
        let closest = Infinity
        let pair = ''
        const out = [...npcs.values()].filter((n) => !n.indoors)
        for (let i = 0; i < out.length; i++) {
          for (let j = i + 1; j < out.length; j++) {
            const a = out[i]
            const b = out[j]
            if (a === undefined || b === undefined) continue
            const d = Math.hypot(a.walker.x - b.walker.x, a.walker.z - b.walker.z)
            if (d < closest) {
              closest = d
              pair = `${a.name}/${b.name}`
            }
          }
        }
        return { closest: out.length < 2 ? -1 : closest, pair, outdoors: out.length }
      },
    }
  }

  const node = townNode()
  if (node === null) return
  const centre = townCentre(node)
  registerAngle({ name: 'town-morning', sceneId: node.def.id, target: centre, rot: 0, zoom: 2 })
  registerAngle({ name: 'town-night', sceneId: node.def.id, target: centre, rot: 0, zoom: 2 })

  /** Midday: everybody out. Returns how many neighbours are on the street. */
  registerDemo('routine-day', async () => {
    clock.setHour(12)
    sweepNow(true)
    await settle(4)
    let out = 0
    for (const n of npcs.values()) if (!n.indoors) out++
    return out
  })

  /** Small hours: everybody in. Returns how many are genuinely off the street. */
  registerDemo('routine-night', async () => {
    clock.setHour(2)
    sweepNow(true)
    await settle(4)
    let inside = 0
    for (const n of npcs.values()) if (n.indoors) inside++
    return inside
  })
}
